import { useContentApi } from "../../../../common/data/data-hooks/content/useContentApi";
import { ContentReferenceType } from "../../../../common/types/ContentReferenceType";
import { ContentSubtype } from "../../../../common/types/ContentSubtype";
import { ContentAssociationDetails } from "../types/ContentAssociationDetails";
import { ContentAssociationFormState } from "../types/ContentAssociationFormState";

interface UseContentOperationsProps {
    setContentDetails: (details?: ContentAssociationDetails) => void;
}

export const useAssociateContentState = ({ setContentDetails }: UseContentOperationsProps) => {
    const { getMovieById, getTvSeriesById, getAncillaryById } = useContentApi();

    const initContentState = async (defaultContentState?: ContentAssociationFormState) => {
        if (!defaultContentState) {
            return;
        }

        const { contentType: type, contentId } = defaultContentState;

        if (!type || !contentId) {
            return;
        }

        switch (type) {
            case ContentReferenceType.Movie:
                await setMovie(contentId);
                break;
            case ContentReferenceType.TvSeries:
                await setTvSeries(contentId);
                break;
            case ContentReferenceType.Ancillary:
                await setAncillary(contentId);
                break;
            default:
                break;
        }
    };

    const setContentState = async (contentId: string, contentType: ContentReferenceType) => {
        switch (contentType) {
            case ContentReferenceType.Movie:
                await setMovie(contentId);
                break;
            case ContentReferenceType.TvSeries:
                await setTvSeries(contentId);
                break;
            case ContentReferenceType.Ancillary:
                await setAncillary(contentId);
                break;
            default:
                break;
        }
    };

    const setMovie = async (movieId: string) => {
        const movie = await getMovieById(movieId);

        setContentDetails({
            type: ContentReferenceType.Movie,
            id: movie.id,
            subtype: movie.subtype,
            source: movie.source,
            title: movie.title,
            versions: movie.versions
        });
    };

    const setAncillary = async (ancillaryId: string) => {
        const result = await getAncillaryById(ancillaryId);

        setContentDetails({
            type: ContentReferenceType.Ancillary,
            id: result.id,
            subtype: result.subtype,
            title: result.title,
            source: result.source,
            isMerged: !!result.mergedTargetId,
            version: result.version
        });
    };

    const setTvSeries = async (tvSeriesId: string) => {
        const result = await getTvSeriesById(tvSeriesId);

        setContentDetails({
            type: ContentReferenceType.TvSeries,
            id: result.id,
            title: result.title,
            source: result.source,
            seasons: result.seasons,
            orphaned: result.orphaned,
            subtype: ContentSubtype.TV
        });
    };

    return {
        initContentState,
        setContentState
    };
};
